import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import { Toaster } from './ui/toaster';
import axios from 'axios';
import HCaptcha from '@hcaptcha/react-hcaptcha';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '10000000-ffff-ffff-ffff-000000000001';

const NAME_REGEX = /^[A-Za-z][A-Za-z'\- ]*$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Defined OUTSIDE component so it never gets recreated on re-render
const Field = ({ id, label, error, children }) => (
  <div>
    <Label htmlFor={id} className="text-slate-700 dark:text-slate-300">{label}</Label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const ContactForm = () => {
  const [fields, setFields] = useState({
    firstName: '', lastName: '', email: '', subject: '', message: ''
  });
  const [errors, setErrors] = useState({});
  const [emailChecking, setEmailChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields(prev => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let err = null;
    if (name === 'firstName' || name === 'lastName') {
      if (!NAME_REGEX.test(value.trim())) err = 'Must start with a letter and contain only letters.';
    }
    if (name === 'subject' && !value.trim()) err = 'Subject is required.';
    if (name === 'message' && !value.trim()) err = 'Message is required.';
    setErrors(prev => ({ ...prev, [name]: err }));
  };

  const handleEmailBlur = async () => {
    const email = fields.email.trim();
    if (!EMAIL_REGEX.test(email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address.' }));
      return;
    }
    setErrors(prev => ({ ...prev, email: null }));
    setEmailChecking(true);
    try {
      const domain = email.split('@')[1];
      const res = await fetch(`https://api.mailcheck.ai/domain/${domain}`);
      const data = await res.json();
      if (data.mx === false) {
        setErrors(prev => ({ ...prev, email: 'This email domain cannot receive emails.' }));
      }
    } catch {
      // network fail — allow through
    } finally {
      setEmailChecking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!NAME_REGEX.test(fields.firstName.trim())) errs.firstName = 'Must start with a letter and contain only letters.';
    if (!NAME_REGEX.test(fields.lastName.trim())) errs.lastName = 'Must start with a letter and contain only letters.';
    if (!EMAIL_REGEX.test(fields.email.trim())) errs.email = 'Please enter a valid email address.';
    if (!fields.subject.trim()) errs.subject = 'Subject is required.';
    if (!fields.message.trim()) errs.message = 'Message is required.';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    if (!captchaToken) {
      toast({ title: 'Verification required', description: 'Please complete the captcha.', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: `${fields.firstName.trim()} ${fields.lastName.trim()}`,
        email: fields.email.trim(),
        subject: fields.subject.trim(),
        message: fields.message,
        captcha_token: captchaToken
      };
      const response = await axios.post(`${API}/contact`, payload);
      if (response.data.success) {
        toast({ title: 'Message Sent!', description: response.data.message });
        setFields({ firstName: '', lastName: '', email: '', subject: '', message: '' });
        setErrors({});
        setCaptchaToken(null);
        captchaRef.current?.resetCaptcha();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send message. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Card className="border-slate-200 dark:border-slate-800">
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>

            <div className="grid grid-cols-2 gap-3">
              <Field id="firstName" label="First Name" error={errors.firstName}>
                <Input
                  id="firstName" name="firstName"
                  value={fields.firstName} onChange={handleChange} onBlur={handleBlur}
                  placeholder="Jane"
                  className={`mt-1 ${errors.firstName ? 'border-red-500' : ''}`}
                />
              </Field>
              <Field id="lastName" label="Last Name" error={errors.lastName}>
                <Input
                  id="lastName" name="lastName"
                  value={fields.lastName} onChange={handleChange} onBlur={handleBlur}
                  placeholder="Smith"
                  className={`mt-1 ${errors.lastName ? 'border-red-500' : ''}`}
                />
              </Field>
            </div>

            <Field id="email" label="Email" error={errors.email}>
              <div className="relative mt-1">
                <Input
                  id="email" name="email" type="email"
                  value={fields.email} onChange={handleChange} onBlur={handleEmailBlur}
                  placeholder="jane@company.com"
                  className={errors.email ? 'border-red-500' : ''}
                />
                {emailChecking && (
                  <span className="absolute right-3 top-2.5 text-xs text-slate-400">Verifying…</span>
                )}
              </div>
            </Field>

            <Field id="subject" label="Subject" error={errors.subject}>
              <Input
                id="subject" name="subject"
                value={fields.subject} onChange={handleChange} onBlur={handleBlur}
                placeholder="Job Opportunity – Senior Engineer"
                className={`mt-1 ${errors.subject ? 'border-red-500' : ''}`}
              />
            </Field>

            <Field id="message" label="Message / Job Description" error={errors.message}>
              <Textarea
                id="message" name="message"
                value={fields.message} onChange={handleChange} onBlur={handleBlur}
                placeholder={`Paste the full job description here.\nBullets and line breaks are preserved.\n\n• 5+ years of experience...\n• Strong knowledge of AWS...`}
                rows={12}
                className={`mt-1 text-sm resize-y leading-relaxed ${errors.message ? 'border-red-500' : ''}`}
              />
              <p className="text-xs text-slate-400 mt-1">{fields.message.length} characters</p>
              {/* Live preview */}
              {fields.message.trim() && (
                <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-slate-50 dark:bg-slate-900 max-h-64 overflow-y-auto">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Preview</p>
                  <div className="text-sm text-slate-700 dark:text-slate-300 space-y-1">
                    {fields.message.split('\n').map((line, i) => (
                      <p key={i} className={`${line.trim() === '' ? 'h-3' : ''} leading-relaxed`}>
                        {line || '\u00A0'}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </Field>

            {/* hCaptcha */}
            <div className="flex justify-center">
              <HCaptcha
                sitekey={HCAPTCHA_SITE_KEY}
                onVerify={(token) => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken(null)}
                ref={captchaRef}
                theme="dark"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting || emailChecking || !captchaToken}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSubmitting ? 'Sending…' : 'Send Message'}
            </Button>
          </form>
        </CardContent>
      </Card>
      <Toaster />
    </>
  );
};

export default ContactForm;
